import * as ngCdkDragDrop from '@angular/cdk/drag-drop'
import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import { escapeRegExp } from '@attain-apps/string-utils'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'
import { ComponentStore } from '@ngrx/component-store'
import * as gridstack from 'gridstack'

import * as state from '../../../../../../../../../state'

import { State } from './state'

@ngCore.Injectable()
export class Store extends ComponentStore<State> {
  /** @see {@link state.mosaic.dev.pages.Collection} */
  private readonly _pagesEc = ngCore.inject(state.mosaic.dev.pages.Collection)

  /** @see {@link state.mosaic.dev.pages.Collection} */
  private readonly _widgetsEc = ngCore.inject(
    state.mosaic.dev.widgets.Collection,
  )

  /** Selector for {@link State.segmentLookup} */
  public readonly segmentLookupSg = this.selectSignal(
    _state => _state.segmentLookup,
  )

  /** Selector for {@link State.editingWidgetFrameId} */
  public readonly editingWidgetFrameIdSg = this.selectSignal(
    _state => _state.editingWidgetFrameId,
  )

  /**
   * Selector that returns array of {@link state.mosaic.dev.pages.models.Segment}
   */
  private readonly _segmentsSg = ngCore.computed(
    (lookup = this.segmentLookupSg()) =>
      Object.values(lookup).filter(TypeChecker.isNotNullish.raw),
  )

  /**
   * Selector that returns array of {@link state.mosaic.dev.pages.models.Segment} sorted based on their `index`
   */
  public readonly sortedSegmentsSg = ngCore.computed(
    (segments = this._segmentsSg()) =>
      segments.sort((a, b) => a.index - b.index),
  )

  /** Selector for {@link State.widgetFrameLookup} */
  public widgetFrameLookupSg = this.selectSignal(
    _state => _state.widgetFrameLookup,
  )

  /** Selector for {@link State.currentSegmentId} */
  public readonly currentSegmentIdSg = this.selectSignal(
    _state => _state.currentSegmentId,
  )

  /** Extracted keys from {@link State.segmentLookup} */
  private readonly _segmentLookupKeysSg = ngCore.computed(
    (segmentLookup = this.segmentLookupSg()) => Object.keys(segmentLookup),
  )

  /** Returns the number of segment lookup keys*/
  private readonly _segmentLookupKeyCountSg = ngCore.computed(
    (segmentLookupKeys = this._segmentLookupKeysSg()) =>
      segmentLookupKeys.length,
  )
  /**
   * Indicates whether the current page builder has more than one {@link state.mosaic.dev.pages.models.Segment}.
   * Used to determine whether to display the segment sidebar.
   */
  public readonly hasSegmentsSg = ngCore.computed(
    (segmentLookupKeyCount = this._segmentLookupKeyCountSg()) =>
      segmentLookupKeyCount > 1,
  )

  /** Selector for {@link State.editingSegment} */
  public editingSegmentSg = this.selectSignal(_state => _state.editingSegment)

  /** Selector for {@link State.editingSegmentId} */
  public editingSegmentIdSg = this.selectSignal(
    _state => _state.editingSegmentId,
  )

  /** Selector {@link State.presentOverflowActions} */
  public readonly presentOverflowActionsSg = this.selectSignal(
    _state => _state.presentOverflowActions,
  )

  /** Selector for {@link State.grid} */
  public readonly gridSg = this.selectSignal(_state => _state.grid)

  /** Selector for {@link State.switchingSegmentConfiguration} */
  public switchingSegmentConfigurationSg = this.selectSignal(
    _state => _state.switchingSegmentConfiguration,
  )

  /** Selector for {@link State.markedForDiscard} */
  public markedForDiscardSg = this.selectSignal(
    _state => _state.markedForDiscard,
  )

  /** @see {@link PAGE_BUILDER_SELECTION_TARGET} */
  public readonly widgetSelectionTarget =
    state.mosaic.dev.widgets.PAGE_BUILDER_SELECTION_TARGET

  constructor() {
    super(State.INITIAL)
    /**
     * Whenever the editing segment id is changed, update the editing segment
     */
    ngCore.effect(() => {
      const editingSegmentId = this.editingSegmentIdSg()

      if (editingSegmentId) {
        this.patchState(() => ({
          editingSegment: this.segmentLookupSg()[editingSegmentId],
        }))
      } else {
        this.patchState({ editingSegment: {} })
      }
    })
  }

  /**
   * Initializes a new {@link State.grid } instance on demand.
   *
   * __NOTE:__ Since {@link GridStack} has internal lifecycle constraints and cannot be instantiated freely,
   * this method allows us to initialize {@link State.grid} at the appropriate point in the component lifecycle.
   */
  public initializeGrid() {
    // Initialize the GridStack instance
    const grid = gridstack.GridStack.init()

    const page = this._pagesEc.plugins.currentEntityPlugin.feature.selectors
      .getEditedEntity()
      .asSignal()

    if (!page?.content) {
      // No page content found, create a new page with initial segment
      this.patchState({ grid })
      this.createSegment()
    } else {
      // Existing page content found, update grid, segment lookup and widgetFrameLookup
      const { segmentLookup, widgetFrameLookup } =
        state.mosaic.dev.pages.models.Segment.Lookup.fromContent(
          page.content,
          grid.getCellHeight(),
        )

      this.patchState({
        grid,
        segmentLookup,
        widgetFrameLookup,
      })

      // Select and set the first segment if available
      const firstSegment = this.sortedSegmentsSg()[0]

      if (firstSegment) {
        this.setCurrentSegment(firstSegment)
      }
    }
  }

  /**
   * Updates the state with the ID of the widget frame currently being edited.
   * Pass `undefined` to clear the selection.
   */
  public setEditingWidget(
    id?: state.mosaic.dev.pages.models.widget.Frame.Id,
    /** Rendering widget alias */
    alias?: string,
  ) {
    // Setting editing widget frame id
    this.patchState({ editingWidgetFrameId: id })

    // Setting editing widget in selection plugin if widgetFrameId and rendering
    // alias exists
    if (id !== undefined && alias) {
      this._widgetsEc.plugins.selectionPlugin.applied.actions.putInSelectionLookup
        .from('PageBuilder.WidgetFrame.Component.Edit.Button.Clicked')
        .dispatch({
          key: this.widgetSelectionTarget,
          value: {
            set: [alias],
          },
        })
    } else {
      // Otherwise unselect editing widget
      this._widgetsEc.plugins.selectionPlugin.applied.actions.putInSelectionLookup
        .from('WidgetFrameSelector.Component.SaveBtn.Clicked')
        .dispatch({
          key: this.widgetSelectionTarget,
          value: { release: true },
        })
    }
  }

  /**
   * Adds a new widget frame to the current segment's layout.
   */
  public addWidgetFrame(): void {
    this.patchState(({ currentSegmentId, grid }) => {
      // Abort if no active segment is selected or grid is not initialized
      if (!currentSegmentId || !grid) {
        return {}
      }

      // Add a new widget frame to the grid
      grid.addWidget(
        state.mosaic.dev.pages.models.widget.Frame.generate({
          autoPosition: true,
        }),
      )
      return { grid }
    })
  }

  /**
   * Removes a widget frame from the current segment's layout by its ID.
   */
  public removeWidgetFrame(id: state.mosaic.dev.pages.models.widget.Frame.Id) {
    this.patchState(({ grid, currentSegmentId }) => {
      // Find the widget frame in the grid by its `gs-id`(identifier generated by GridStack library).
      const widgetFrameEl = grid
        ?.getGridItems()
        ?.find(widgetFrame => widgetFrame.getAttribute('gs-id') === id)

      // Exit early if widget is not found or no segment is currently active or grid is not initialized
      if (!widgetFrameEl || !currentSegmentId || !grid) {
        return {}
      }

      // Remove widget frame from grid
      grid.removeWidget(widgetFrameEl)

      return { grid }
    })
  }

  /**
   * Saves the current grid state to the selected segment, generating
   * a new lookup entry.
   */
  private _generateSegmentLookup(
    segmentLookup: state.mosaic.dev.pages.models.Segment.Lookup,
    grid?: gridstack.GridStack,
    currentSegmentId?: state.mosaic.dev.pages.models.Segment.Id,
  ): state.mosaic.dev.pages.models.Segment.Lookup {
    segmentLookup = { ...segmentLookup }
    if (!grid || !currentSegmentId || !segmentLookup[currentSegmentId]) {
      return segmentLookup
    }

    const result = grid.save()

    // We want to save the layout *only* if result is an array of GridStackWidget.
    if (TypeChecker.isArray(result)) {
      segmentLookup[currentSegmentId].widgetFrames = result
    }

    return segmentLookup
  }

  /**
   * Saves the layout of the currently active segment.
   *
   * Intended for use when a user explicitly saves, or before other operations that
   * require the current layout to be retained.
   */
  public saveCurrentSegmentLayout() {
    this.patchState(({ grid, currentSegmentId, segmentLookup }) => {
      const updatedLookup = this._generateSegmentLookup(
        segmentLookup,
        grid,
        currentSegmentId,
      )
      return { segmentLookup: updatedLookup }
    })
  }

  /** Updates state for editing segment */
  public updateEditingSegment(
    editingSegmentUpdates: Partial<state.mosaic.dev.pages.models.Segment>,
  ) {
    this.patchState(({ editingSegment }) => ({
      editingSegment: {
        ...editingSegment,
        ...editingSegmentUpdates,
      },
    }))
  }

  /** Updates the segment lookup with modified segment data */
  public saveEditingSegment(): void {
    const editingSegment = this.editingSegmentSg()
    const editingSegmentId = editingSegment.id

    // At this point editingSegmentId should be defined, but for nullish type
    // we need this check
    if (!editingSegmentId) {
      return
    }

    TypeAsserter.assertIsNotNullish(
      editingSegment.alias,
      'Segment alias is not defined',
    )

    this.patchState(({ segmentLookup }) => {
      segmentLookup = { ...segmentLookup }
      const _segment = segmentLookup[editingSegmentId]

      if (_segment) {
        segmentLookup[editingSegmentId] = {
          ..._segment,
          ...this.editingSegmentSg(),
        }
      }

      return { editingSegmentId: undefined, segmentLookup }
    })
  }

  /**
   * Loads a specific segment’s layout into the grid.
   * This method performs operations:
   * 1. Saves the current segment layout if one exists
   * 2. Loads a new segment layout into the grid
   * 3. Set new segment as the current segment
   *
   * This method is essential for segment switching while maintaining layout integrity.
   */
  private _loadSegmentLayout({
    id,
    widgetFrames,
  }: state.mosaic.dev.pages.models.Segment) {
    this.patchState(({ grid, currentSegmentId, segmentLookup }) => {
      if (!grid || currentSegmentId === id) {
        return {}
      }

      const updatedLookup = this._generateSegmentLookup(
        segmentLookup,
        grid,
        currentSegmentId,
      )

      // Load a new segment layout into the grid.
      // NOTE GridStack's `load()` expects a mutable array — we cast it explicitly to avoid issues,
      // since `widgetFrames` is defined as `readonly` for safety elsewhere.
      grid.load(widgetFrames as Mutable<typeof widgetFrames>)

      return {
        currentSegmentId: id,
        grid,
        segmentLookup: updatedLookup,
      }
    })
  }

  /**
   * Creates a new segment and selects it.
   */
  public createSegment() {
    const segments = this._segmentsSg()
    const segmentName = _generateSegmentName(
      segments.map(segment => segment.name),
    )
    const maxSegmentIndex = Math.max(
      -1,
      ...segments.map(segment => segment.index),
    )

    const newSegment = state.mosaic.dev.pages.models.Segment.generate({
      alias:
        state.mosaic.dev.pages.models.Segment.generateAliasFromName(
          segmentName,
        ),
      // To position the new segment at the end, it needs the next available highest index
      index: maxSegmentIndex + 1,
      name: segmentName,
    })

    this.patchState(({ segmentLookup }) => {
      segmentLookup = { ...segmentLookup }
      segmentLookup[newSegment.id] = newSegment
      return {
        segmentLookup,
      }
    })
    this._loadSegmentLayout(newSegment)
  }

  /**
   * Updates the segments in the store by either:
   *  - providing a {@link state.mosaic.dev.pages.models.Segment[]} (which will be re-indexed), or
   *  - providing a {@link state.mosaic.dev.pages.models.Segment.Lookup} directly.
   *
   */
  public setSegments({
    segments,
    lookup,
  }: ExactlyOneOf<{
    segments: state.mosaic.dev.pages.models.Segment[]
    lookup: state.mosaic.dev.pages.models.Segment.Lookup
  }>): void {
    let updatedLookup: state.mosaic.dev.pages.models.Segment.Lookup = {}

    if (segments) {
      // Re-index segments based on their position in the array
      segments.forEach((segment, index) => {
        updatedLookup[segment.id] = { ...segment, index }
      })
    } else {
      updatedLookup = lookup
    }

    this.patchState({ segmentLookup: updatedLookup })
  }

  /**
   *  Reorders the segments based on drag-and-drop movement and updates the store.
   */
  public reorderSegments(from: number, to: number): void {
    const segments = [...this.sortedSegmentsSg()]
    // Reorders segment array in place
    ngCdkDragDrop.moveItemInArray(segments, from, to)
    // Re-index and update state
    this.setSegments({ segments })
  }

  /**
   * Loads segment associated layout into the grid
   */
  public setCurrentSegment(segment?: state.mosaic.dev.pages.models.Segment) {
    if (segment) {
      this._loadSegmentLayout(segment)
    }
  }

  /**
   * Deletes a segment and updates the current segment selection if necessary.
   */
  public deleteSegment(id: state.mosaic.dev.pages.models.Segment.Id) {
    this.patchState(({ segmentLookup }) => {
      segmentLookup = { ...segmentLookup }
      delete segmentLookup[id]

      return { segmentLookup }
    })
    if (this.currentSegmentIdSg() === id) {
      const firstSegment = this.sortedSegmentsSg()[0]
      if (firstSegment) {
        this._loadSegmentLayout(firstSegment)
      }
    }

    if (this.editingSegmentIdSg() === id) {
      this.patchState({ editingSegmentId: undefined })
    }
  }

  /**
   * Updates the {@link ents.core.mosaic.Widget.Ref} for the currently edited widget frame.
   */
  public applyWidgetRefToEditingWidgetFrame(
    widgetRef: ents.core.mosaic.Widget.Ref,
  ): void {
    const editingWidgetFrameId = this.editingWidgetFrameIdSg()
    if (editingWidgetFrameId) {
      this.patchState({
        widgetFrameLookup: {
          ...this.widgetFrameLookupSg(),
          [editingWidgetFrameId]: widgetRef,
        },
      })
    }
  }
}

/**
 * Generates a unique segment name that doesn't conflict with the given list of existing names.
 * It uses a base name (e.g., "New Segment") and appends a number in parentheses if needed.
 *
 * @example:
 *  - existing = []                         → returns "New Segment"
 *  - existing = ["New Segment"]            → returns "New Segment (1)"
 *  - existing = ["New Segment (1)", ...]   → returns "New Segment"
 *  - existing = ["New Segment", "New Segment (1)", "New Segment (2)"] → returns "New Segment (3)"
 */
function _generateSegmentName(existing: string[]): string {
  // The default base name for new segments
  const defaultName = state.mosaic.dev.pages.models.Segment.DEFAULT_NAME

  // Regex pattern to match "New Segment" and "New Segment (X)", capturing the number if present
  const pattern = new RegExp(`^${escapeRegExp(defaultName)}(?: \\((\\d+)\\))?$`)

  // Extract used numeric suffixes from matching names and store in a Set
  const used = new Set(
    existing
      .map(name => {
        const match = name.match(pattern)
        // If the name matches the pattern, extract the number (default to 0 if none)
        return match ? parseInt(match[1] ?? '0', 10) : null
      })
      .filter((n): n is number => n !== null), // Filter out null values
  )

  // Find the smallest unused number to use as the next suffix
  let suffix = 0
  while (used.has(suffix)) {
    suffix++
  }

  // Return the base name if suffix is 0, otherwise append the suffix in parentheses
  return suffix === 0 ? defaultName : `${defaultName} (${suffix})`
}
